import IconPickerWithRemoveButton from 'client/components/core/Sidebar/components/Settings/components/IconPickerWithRemoveButton'
import Input from 'client/components/core/Sidebar/components/Settings/components/Input/Input'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import {
  FontSize,
  LineHeight,
  ShadowStyle,
  TextAlignStyle,
} from 'client/components/core/Sidebar/components/Settings/styles'
import Border from 'client/components/core/Sidebar/components/Settings/styles/Border'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { usePage } from 'client/store'
import { pageSelectors } from 'client/store/page/pageSelectors'
import { FieldSlugEnum } from 'common/enums/FieldSlugEnum'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import { FieldInterface } from 'common/types/entities/field-interface'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { contactFieldsQuery } from 'src/editor/api/queries/contact-fields-query'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import {
  OptionType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { getContactFieldTitle } from 'src/editor/shared/utils/get-contact-field-title'
import { ContactSlugEnum } from 'src/shared/enums/contact-fields-enum'

const FieldSettings = ({ entity }: { entity: FieldInterface }) => {
  const { t } = useTranslation()
  const { isMobile } = useDeviceModeContext()
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<FieldInterface>()
  const pageType = usePage(pageSelectors.getPageType)
  const isLoginPage =
    pageType === PageTypeEnum.CustomLogin ||
    pageType === PageTypeEnum.WebsiteLogin
  const isCountryType = entity.slug === FieldSlugEnum.Country
  const { data: contactFields, isFetching } = contactFieldsQuery.useQuery()
  const [inputTypes, setInputTypes] = useState<OptionType[]>([])

  const defaultOption = useMemo(
    () => ({
      label: t('entity_settings.field.select_label'),
      value: '',
    }),
    [t],
  )
  const [selectedInputType, setSelectedInputType] =
    useState<OptionType>(defaultOption)

  useEffect(() => {
    if (!contactFields?.length) {
      return
    }

    const inputTypeOptions = [defaultOption].concat(
      contactFields.map(inputType => ({
        label: getContactFieldTitle(inputType, t),
        value: inputType.slug,
      })),
    )

    setInputTypes(inputTypeOptions)

    if (entity.slug) {
      const selectedType =
        inputTypeOptions.find(option => option.value === entity.slug) ??
        defaultOption
      setSelectedInputType(selectedType)
    }
  }, [contactFields, defaultOption])

  const isMandatoryType = entity.slug === FieldSlugEnum.Email
  const isOptionalType =
    entity.slug === FieldSlugEnum.TaxNumber ||
    entity.slug === FieldSlugEnum.CompanyName

  const isOptionalSettingAvailable =
    !isLoginPage && !isMandatoryType && !isOptionalType

  const updateInputType = (selectedOption: SingleValue<OptionType>) => {
    if (!selectedOption) {
      return
    }

    const slug = selectedOption.value as ContactSlugEnum
    const placeholder = selectedOption.label
    let optional = false

    if (
      selectedOption.value === FieldSlugEnum.CompanyName ||
      selectedOption.value === FieldSlugEnum.TaxNumber
    ) {
      optional = true
    }

    setSelectedInputType(selectedOption)

    updateEntity({
      ...entity,
      slug,
      placeholder,
      optional,
    })
  }

  return (
    <>
      {!isLoginPage && (
        <SelectNew
          labelText="Input type"
          isLoading={isFetching}
          onChange={updateInputType}
          options={inputTypes}
          selectedOption={selectedInputType}
          skipTranslation
        />
      )}

      {!isCountryType && (
        <Input
          label="entity_settings.field.placeholder.label"
          value={entity.placeholder}
          update={updateProp('placeholder')}
          type="text"
        />
      )}
      <TextAlignStyle
        textAlign={entity.textAlign ?? 'left'}
        mobileTextAlign={entity.mobileTextAlign ?? entity.textAlign ?? 'left'}
        update={updateProp('textAlign')}
        mobileUpdate={updateProp('mobileTextAlign')}
      />
      <ColorPickerControl
        label="entity_settings.field.placeholder_color"
        update={updateProp('placeholderColor')}
        mobileUpdate={updateProp('mobilePlaceholderColor')}
        color={entity.placeholderColor}
        mobileColor={entity.mobilePlaceholderColor}
        isClearable
      />
      {isOptionalSettingAvailable && (
        <CheckboxControl
          label="entity_settings.field.optional"
          tooltipText="entity_settings.field.optional_popover"
          value={entity.optional}
          update={updateProp('optional')}
        />
      )}
      <GroupTitle>typography</GroupTitle>
      <FontSize
        update={updateProp('fontSize')}
        mobileUpdate={updateProp('mobileFontSize')}
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
      />
      <LineHeight
        lineHeight={entity.lineHeight}
        mobileLineHeight={entity.mobileLineHeight}
        update={updateProp('lineHeight')}
        mobileUpdate={updateProp('mobileLineHeight')}
      />
      {!isCountryType && (
        <IconPickerWithRemoveButton
          label="entity_options.icon_picker.title"
          value={entity.iconClassName}
          update={updateProp('iconClassName')}
        />
      )}
      <GroupTitle>color</GroupTitle>
      <ColorPickerControl
        label="settings_styles.color.label"
        update={updateProp('color')}
        mobileUpdate={updateProp('mobileColor')}
        color={entity.color}
        mobileColor={entity.mobileColor}
        isClearable
      />
      <ColorPickerControl
        label="settings_styles.background_color.label"
        update={updateProp('backgroundColor')}
        mobileUpdate={updateProp('mobileBackgroundColor')}
        color={entity.backgroundColor}
        mobileColor={entity.mobileBackgroundColor}
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <Border
        update={updateProp(isMobile ? 'mobileBorder' : 'border')}
        border={isMobile ? entity.mobileBorder : entity.border}
      />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default FieldSettings
